import { Alert } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { PowerBIEmbed } from 'powerbi-client-react'
import { Report } from 'powerbi-client'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { powerBiConfigDefaults, PowerBiFilter } from '../constants/power-bi'
import { ReportWithToken } from '../hooks/queries/reports/use-power-bi-report-with-token'
import { getPowerBiPoolName } from '../utils/get-power-bi-pool-name'

interface PowerBiReportFilter {
  $schema: string
  filterType: number
  target: {
    column: string
    table: string
  }
  operator: string
  values: [string]
}

export function PowerBiReportRenderer({
  report,
  defaultFilters,
  customValue,
}: {
  report: ReportWithToken
  defaultFilters: PowerBiFilter
  customValue?: string | null
}) {
  const [powerBiError, setPowerBiError] = useState<boolean>(false)
  const { pool } = useParams()
  const queryClient = useQueryClient()
  const reportRef = useRef<Report | null>(null)

  const applyFilters = async () => {
    if (!reportRef.current) return

    const PowerBiPoolName = getPowerBiPoolName(pool || '')
    const filters: PowerBiReportFilter[] = [
      {
        ...defaultFilters,
        operator: 'In',
        values: [customValue || PowerBiPoolName || ''],
      },
    ]

    try {
      await reportRef.current.setFilters(filters)
    } catch (error) {
      console.error('Failed to apply Power BI filters:', error)
    }
  }

  useEffect(() => {
    applyFilters()
  }, [pool, customValue])

  if (powerBiError) {
    return (
      <Alert severity="error">
        Oops! There was an error rendering the report. Please reload the page or
        try again later.
      </Alert>
    )
  }

  return (
    <PowerBIEmbed
      embedConfig={{
        ...powerBiConfigDefaults,
        id: report.id,
        embedUrl: report.embedUrl,
        accessToken: report.token,
      }}
      cssClassName="power-bi-report"
      getEmbeddedComponent={(embeddedReport) => {
        reportRef.current = embeddedReport as Report
      }}
      eventHandlers={
        new Map([
          [
            'loaded',
            () => {
              applyFilters()
            },
          ],
          [
            'error',
            (event: any) => {
              const errorDetail = event?.detail
              const isTokenExpired =
                errorDetail?.message?.includes('TokenExpired') ||
                errorDetail?.message?.includes('403') ||
                errorDetail?.statusCode === 403

              if (isTokenExpired) {
                // Invalidate cache to trigger token refetch
                queryClient.invalidateQueries({
                  queryKey: ['/reports?reportId'],
                })
              } else {
                setPowerBiError(true)
              }
            },
          ],
        ])
      }
    />
  )
}
